/* eslint-disable no-undef */
import axios, { HttpStatusCode } from 'axios';
import cookies from 'js-cookie';
import { useState } from 'react';
import { errorToaster } from '../../components/toaster/Toaster';
import { ResponseMessage } from '../../constants/Constants';
import { removeAuthToken } from '../../helper/authHelper';

function useBaseAPI() {
  const [isLoading, setIsLoading] = useState(false);

  const getHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    const apiToken = localStorage.getItem('authToken');
    if (apiToken) {
      headers.Authorization = `Bearer ${apiToken}`;
    }

    const adUserId = cookies.get('adUserId');
    if (adUserId && adUserId !== '') {
      headers.aduserid = adUserId;
    }

    const userId = cookies.get('userId');
    if (userId && userId !== '' && parseInt(userId, 10) > 0) {
      headers.userId = userId;
    }

    const adminCookie = cookies.get('admin');
    headers['x-user-type'] = adminCookie;

    return headers;
  };

  const client = async (method, uri, data = {}, config = {}) => {
    const authRedirect = !(config?.authRedirect === false);
    const apiUrl = config?.baseUrl?.length ? config?.baseUrl : process.env.REACT_APP_API_ENDPOINT;
    const withCredentials =
      typeof config?.withCredentials === 'undefined' ? true : config?.withCredentials;
    const headers = getHeaders();

    const axiosObj = axios.create({
      baseURL: apiUrl,
      timeout: 31000,
      headers,
      withCredentials,
    });

    const axiosConfig = { baseURL: apiUrl, timeout: 31000, headers, withCredentials, ...config };

    axiosObj.interceptors.request.use(
      (configuration) => {
        setIsLoading(true);
        return configuration;
      },
      (error) => {
        errorToaster('Error');
        setIsLoading(false);
        return Promise.reject(error);
      },
    );

    axiosObj.interceptors.response.use(
      (response) => {
        setIsLoading(false);
        return response;
      },
      (error) => {
        errorToaster(error?.response?.data?.message || 'Error');
        setIsLoading(false);
        return Promise.reject(error);
      },
    );

    return axiosObj[method](uri, method === 'get' ? axiosConfig : data, axiosConfig).then(
      (response) => {
        if (response?.data?.success) {
          return response.data;
        }
        if (response?.data?.success === false) {
          return new Promise((resolve, reject) => {
            errorToaster(
              response?.data?.message || response?.data?.error || ResponseMessage.ERROR_DEFAULT,
            );
            return reject(response.data.message);
          });
        }
        return response?.data;
      },
      (error) => {
        // request level error
        let errorData = { status: false, message: 'Unknown Error' };
        try {
          if (error.response.data) {
            errorData = error.response.data;
          }
        } catch (e) {
          errorData.message = 'Server not Reachable';
        }
        if (error.request.status === HttpStatusCode.Unauthorized && authRedirect) {
          removeAuthToken()
          window.location.href = '/login';
        } else if (error.request.status === HttpStatusCode.Forbidden) {
          window.location.href = '/';
        }
        return new Promise((resolve, reject) => {
          // remain to write logic for giving message according to error status code
          reject(errorData);
        });
      },
    );
  };

  const apiURL = process.env.REACT_APP_API_ENDPOINT
  return { isLoading, client, apiURL };
}

export default useBaseAPI;
